import { SearchOutlined } from "@ant-design/icons";
import { Avatar, Col, DatePicker, Flex, Input, Row, Spin, Table, Typography } from "antd";
import React, { useState } from "react";

import type { TableColumnsType, TablePaginationConfig, TableProps } from "antd";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { debounce } from "lodash";
import { useIsMobileView } from "../../hooks/ui-hook";
import { AnalyticsObject, useGetUserAnalyticsQuery } from "../../service/analytics.service";
import { getFirstLetterOfNameAndSurname } from "../../utils/datamanipulation.utils";
import { formatSeconds, getRandomAvatarColor } from "../../utils/ui.utils";
import styles from "./analytics.module.scss";

const { RangePicker } = DatePicker;

// Using real API data; no mock generator

const getColumns = (
  sorterInfo: {
    field?: string;
    order?: "ascend" | "descend" | null;
  },
  isRangeSelected: boolean,
): TableColumnsType<AnalyticsObject> => [
  {
    key: "index",
    render: (_text, _record, index) => (
      <Flex align="center" justify="center" style={{ padding: 6 }}>
        {index + 1}
      </Flex>
    ),
  },
  {
    title: "Name",
    dataIndex: "name",
    width: 350,
    sorter: true,
    sortOrder: sorterInfo.field === "name" ? sorterInfo.order : null,
    render: (value, record) => {
      return (
        <Flex align="center" gap={16}>
          <Avatar
            size={40}
            style={{
              backgroundColor: getRandomAvatarColor(value),
            }}
          >
            {getFirstLetterOfNameAndSurname(value)}
          </Avatar>
          <Flex vertical>
            <Typography.Text strong>{value}</Typography.Text>
            <Typography.Text>{record.email}</Typography.Text>
          </Flex>
        </Flex>
      );
    },
  },
  {
    title: isRangeSelected ? "Clients Created (Range Total)" : "Clients Created (default: last 30 days)",
    dataIndex: "number_of_clients_created_per_month",
    width: 150,
    sorter: true,
    sortOrder: sorterInfo.field === "number_of_clients_created_per_month" ? sorterInfo.order : null,
    render: (value) => {
      return (
        <Flex align="center">
          <span>{value}</span>
        </Flex>
      );
    },
  },
  {
    title: isRangeSelected ? "Total Notes (Range)" : "Total Notes (default: 7-day Avg.)",
    dataIndex: "total_number_of_notes_created",
    sorter: true,
    sortOrder: sorterInfo.field === "total_number_of_notes_created" ? sorterInfo.order : null,
    render: (value) => {
      return value;
    },
  },
  {
    title: isRangeSelected ? "Intakes Created (Per Month - Range)" : "Intakes Created (default: Per Month)",
    dataIndex: "number_of_intakes_created_per_month",
    width: 150,
    sorter: true,
    sortOrder: sorterInfo.field === "number_of_intakes_created_per_month" ? sorterInfo.order : null,
    render: (value) => {
      return (
        <Flex align="center">
          <span>{value}</span>
        </Flex>
      );
    },
  },
  {
    title: isRangeSelected ? "Active Patients (Range)" : "Active Patients (default: last 14 days)",
    dataIndex: "number_of_ongoing_clients",
    sorter: true,
    sortOrder: sorterInfo.field === "number_of_ongoing_clients" ? sorterInfo.order : null,
    render: (value) => {
      return value;
    },
  },
  {
    title: isRangeSelected ? "Avg Time Per Session (Range)" : "Avg Time Per Session",
    dataIndex: "average_time_per_session",
    width: 150,
    sorter: true,
    sortOrder: sorterInfo.field === "average_time_per_session" ? sorterInfo.order : null,
    render: (value) => {
      if (!value) {
        return "";
      }
      return formatSeconds(value);
    },
  },
  {
    title: "Last Note Created On",
    dataIndex: "last_note_created_on",
    width: 175,
    sorter: true,
    sortOrder: sorterInfo.field === "last_note_created_on" ? sorterInfo.order : null,
    render: (value) => {
      return dayjs(value).format("MMM D, YYYY");
    },
  },
  {
    title: "Joined On",
    dataIndex: "joined_on",
    sorter: true,
    sortOrder: sorterInfo.field === "joined_on" ? sorterInfo.order : null,
    render: (value) => {
      return dayjs(value).format("MMM D, YYYY");
    },
  },
];

const AnalyticsPage = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [ordering, setOrdering] = useState("");
  const [sorterInfo, setSorterInfo] = useState<{ field?: string; order?: "ascend" | "descend" | null }>({
    field: undefined,
    order: null,
  });
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const {
    data: analytics,
    isLoading,
    isFetching,
  } = useGetUserAnalyticsQuery({
    page,
    search: searchValue,
    ordering,
    pageSize,
    start_date: dateRange?.[0] ? dateRange?.[0]?.format("YYYY-MM-DD") : undefined,
    end_date: dateRange?.[1] ? dateRange?.[1]?.format("YYYY-MM-DD") : undefined,
  });

  const isMobile = useIsMobileView();

  const componentName = "analytics";

  // Create a debounced version of setSearchValue
  const debouncedSetSearchValue = debounce((value) => setSearchValue(value), 600);

  const handleSearch: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    debouncedSetSearchValue(event.target.value);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<AnalyticsObject> | SorterResult<AnalyticsObject>[],
  ) => {
    setPage(pagination.current as number);
    setPageSize(pagination.pageSize as number);
    const s = (Array.isArray(sorter) ? sorter[0] : sorter) as any;
    const nextField: string | undefined = (s && (s.columnKey || s.field)) as string | undefined;
    const nextOrder: "ascend" | "descend" | null = (s && s.order) || null;
    setSorterInfo({ field: nextField, order: nextOrder });
    if (nextOrder && nextField) {
      setOrdering(`${nextOrder === "descend" ? "-" : ""}${nextField}`);
      setPage(1);
    } else {
      setOrdering("");
    }
  };

  const baseData: AnalyticsObject[] = analytics?.results ? [...analytics.results] : [];

  const tableData = React.useMemo(() => {
    const data = [...baseData];
    const field = sorterInfo.field;
    const order = sorterInfo.order;
    if (!field || !order) return data;

    const compare = (a: AnalyticsObject, b: AnalyticsObject): number => {
      const dir = order === "ascend" ? 1 : -1;
      switch (field) {
        case "name":
          return dir * (a.name || "").localeCompare(b.name || "");
        case "joined_on":
          return dir * (dayjs(a.joined_on).valueOf() - dayjs(b.joined_on).valueOf());
        case "last_note_created_on":
          return dir * (dayjs(a.last_note_created_on).valueOf() - dayjs(b.last_note_created_on).valueOf());
        case "total_number_of_notes_created":
          return dir * ((a.total_number_of_notes_created || 0) - (b.total_number_of_notes_created || 0));
        case "number_of_intakes_created_per_month":
          return dir * ((a.number_of_intakes_created_per_month || 0) - (b.number_of_intakes_created_per_month || 0));
        case "number_of_clients_created_per_month":
          return dir * ((a.number_of_clients_created_per_month || 0) - (b.number_of_clients_created_per_month || 0));
        case "number_of_ongoing_clients":
          return dir * ((a.number_of_ongoing_clients || 0) - (b.number_of_ongoing_clients || 0));
        case "average_time_per_session":
          return dir * (((a.average_time_per_session as number) || 0) - ((b.average_time_per_session as number) || 0));
        default:
          return 0;
      }
    };

    return data.sort(compare);
  }, [baseData, sorterInfo]);

  const columns = React.useMemo(
    () => getColumns(sorterInfo, Boolean(dateRange?.[0] && dateRange?.[1])),
    [sorterInfo, dateRange],
  );

  return (
    <Row>
      <Col span={24} className={styles[`${componentName}__container-right`]}>
        <Flex justify="space-between" align="center" className={styles[`${componentName}__container-header`]}>
          <Typography className={styles[`${componentName}__container-title`]}>Org Members</Typography>
        </Flex>

        {/* Top controls: Search + Date Range */}
        <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
          <Flex align="center" gap={12}>
            <Input
              placeholder="Search for clinician"
              onChange={handleSearch}
              prefix={<SearchOutlined />}
              size="small"
              style={{ padding: "4px 12px", borderRadius: 6, maxWidth: 260 }}
            />
            <RangePicker
              allowClear
              size="small"
              onChange={(values) => {
                setDateRange(values as [Dayjs | null, Dayjs | null] | null);
                setPage(1);
              }}
            />
          </Flex>
        </Flex>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={tableData}
          loading={isLoading || isFetching}
          onChange={handleTableChange}
          size="middle"
          pagination={{
            total: analytics?.count,
            current: page,
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            position: ["bottomRight"],
          }}
        />
      </Col>
    </Row>
  );
};

export default AnalyticsPage;
